import { useCallback, useEffect, useMemo, useRef } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { useAgentChat } from "./use-agent-chat"
import type { HostedAgentSummary } from "@/api/client/hosted-agents"
import type { DropzoneOptions, FileRejection } from "react-dropzone"

import { removeExplorerContextFile } from "@/features/ai-explorer/utils/context-file-removal"
import { peekSteerComposerText } from "@/features/chat/hooks/use-steer-actions"
import { ChatService } from "@/services/chat-service"
import { useHostedAgentArtifactSeedStore } from "@/stores/hosted-agent-artifact-seed-store"

import { useAgentFileUpload } from "./use-agent-file-upload"
import { useAgentSources } from "./use-agent-sources"
import {
  AGENT_CHAT_DROPZONE_OPTIONS,
  agentSourceRejectionKey,
  appendAgentDraft,
} from "../utils/agent-workspace-utils"

type Props = {
  agent: HostedAgentSummary
  chat: ReturnType<typeof useAgentChat>
  isReader: boolean
  onSessionEnsured?: (sessionId: string) => boolean | void | Promise<boolean | void>
}

/** The single draft/file entry into a durable agent turn. All suggestions remain editable. */
export function useAgentComposer({ agent, chat, isReader, onSessionEnsured }: Props) {
  const { t } = useTranslation()
  const {
    sessionType,
    currentSessionId,
    ensureSession,
    files,
    handleAddFiles,
    handleRemoveFile,
    handleRemoveIndexedFile,
    handleResetFiles,
    handleSendQuestion,
  } = chat
  const workspaceRef = useRef<HTMLDivElement>(null)
  const activeAgentRef = useRef(agent.id)
  activeAgentRef.current = agent.id
  const latestFilesRef = useRef(files)
  latestFilesRef.current = files
  const permanentSources = useAgentSources(agent.slug)
  const taskSources = useAgentSources(agent.slug, currentSessionId, Boolean(currentSessionId))
  const pendingComposerText = useHostedAgentArtifactSeedStore((state) => state.pendingComposerText)
  const setPendingComposerText = useHostedAgentArtifactSeedStore(
    (state) => state.setPendingComposerText
  )

  // The composer keeps its draft locally; we reach it through Chat.Footer's
  // `onSetQuestion` registration and flush any pending pre-fill once ready.
  const setQuestionRef = useRef<((text: string) => void) | null>(null)
  const handleSetQuestion = useCallback((setter: (text: string) => void) => {
    setQuestionRef.current = setter
  }, [])
  const insertDraft = useCallback(
    (text: string) => {
      if (isReader) return
      setQuestionRef.current?.(appendAgentDraft(peekSteerComposerText(), text))
      workspaceRef.current?.querySelector<HTMLTextAreaElement>("textarea")?.focus()
    },
    [isReader]
  )
  useEffect(() => {
    if (pendingComposerText == null || !setQuestionRef.current || isReader) return
    insertDraft(pendingComposerText)
    setPendingComposerText(null)
  }, [pendingComposerText, setPendingComposerText, insertDraft, isReader])

  // --- #3021: attachments ---------------------------------------------------
  // Same composer file stack as AI Explorer; only the transport differs (the
  // files are uploaded to the session before the turn — see the hook).
  const { isUploading, uploadPendingFiles } = useAgentFileUpload({
    agentRef: agent.slug,
    sessionType,
    sessionId: currentSessionId ?? null,
    ensureSession,
    resetFiles: handleResetFiles,
    onSessionEnsured,
  })
  // Legacy indexed attachments stay removable; new sources have versioned controls.
  const handleRemoveComposerFile = useCallback(
    (file: File | string) => {
      void removeExplorerContextFile(file, {
        handleRemoveFile,
        handleRemoveIndexedFile,
      })
    },
    [handleRemoveFile, handleRemoveIndexedFile]
  )
  const handleAddSourceFiles = useCallback(
    (added: File[]) => {
      const capabilities = permanentSources.data?.capabilities
      if (!capabilities || isReader || isUploading) return false
      if (
        added.some(
          (file) =>
            file.size > capabilities.max_file_bytes ||
            !capabilities.accepted_extensions.some((extension) =>
              file.name.toLowerCase().endsWith(extension)
            )
        )
      ) {
        toast.error(t(agentSourceRejectionKey(added)))
        return false
      }
      if (
        (taskSources.data?.sources.length ?? 0) + files.length + added.length >
        capabilities.max_sources
      ) {
        toast.error(t("agents.sources.limit", { count: capabilities.max_sources }))
        return false
      }
      handleAddFiles(added)
    },
    [
      permanentSources.data,
      taskSources.data,
      files.length,
      handleAddFiles,
      isReader,
      isUploading,
      t,
    ]
  )
  // react-dropzone filters by `accept` BEFORE `onDrop`: a refused file (an
  // image dragged in, or forced through the composer's file input) reaches
  // `onDrop` as an empty list, so without this nothing at all happened.
  const handleRejectedSourceFiles = useCallback(
    (rejections: FileRejection[]) => {
      if (rejections.length === 0) return
      toast.error(t(agentSourceRejectionKey(rejections.map((rejection) => rejection.file))))
    },
    [t]
  )
  const sourceDropzoneOptions = useMemo<DropzoneOptions>(
    () => ({ ...AGENT_CHAT_DROPZONE_OPTIONS, onDropRejected: handleRejectedSourceFiles }),
    [handleRejectedSourceFiles]
  )
  const sendingRef = useRef(false)
  const handleComposerSend = useCallback(
    (question: string) => {
      if (sendingRef.current || isReader) return false
      sendingRef.current = true
      void (async () => {
        let targetSessionId = ChatService.getInstance().getOpenSession(sessionType)?.id ?? null
        const stillOwnsDraft = () =>
          Boolean(workspaceRef.current) &&
          activeAgentRef.current === agent.id &&
          (ChatService.getInstance().getOpenSession(sessionType)?.id ?? null) === targetSessionId
        try {
          if (files.length > 0) {
            const uploaded = await uploadPendingFiles(files)
            targetSessionId = uploaded.sessionId
            if (!uploaded.ok) {
              // The composer cleared the draft on send (`clearOnSend`); put it
              // back so a failed upload doesn't cost the user their message.
              if (!stillOwnsDraft()) return
              setQuestionRef.current?.(appendAgentDraft(peekSteerComposerText(), question))
              // Adopting the newly-created session hydrates shared chat state,
              // which clears its transient file list. Keep failed attachments
              // available for retry, not just the text that mentions them.
              const missing = files.filter((file) => !latestFilesRef.current.includes(file))
              if (missing.length) handleAddFiles(missing)
              return
            }
          }
          if (!stillOwnsDraft()) return
          // The attachments never ride the turn: they were uploaded above, and
          // the explicit empty list keeps this send on the durable transport
          // (a turn carrying fresh uploads is forced onto the legacy WebSocket,
          // which refuses hosted-agent sessions with 409).
          await handleSendQuestion({ question, files: [] })
        } catch {
          if (stillOwnsDraft()) {
            setQuestionRef.current?.(appendAgentDraft(peekSteerComposerText(), question))
            toast.error(t("agents.chat.send_failed"), { position: "top-center" })
          }
        } finally {
          sendingRef.current = false
        }
      })()
    },
    [
      agent.id,
      files,
      handleAddFiles,
      handleSendQuestion,
      uploadPendingFiles,
      isReader,
      sessionType,
      t,
    ]
  )

  return {
    workspaceRef,
    permanentSources,
    taskSources,
    isUploading,
    insertDraft,
    handleSetQuestion,
    handleAddSourceFiles,
    handleRemoveComposerFile,
    handleComposerSend,
    handleRejectedSourceFiles,
    sourceDropzoneOptions,
  }
}
